import { useCallback, useEffect, useState } from 'react';
import { AlertTriangle, RefreshCw, Loader2 } from 'lucide-react';
import api from '../../lib/api';
import Button from '../ui/Button';
import { type DocumentSyncStatus } from '../../lib/googleWorkspace';

interface GoogleDocumentUpdateBannerProps {
  jobId: string;
  readOnly?: boolean;
  onReimportBrief?: (documentId: string) => void;
  onRefresh?: () => void;
}

export function GoogleDocumentUpdateBanner({
  jobId,
  readOnly = false,
  onReimportBrief,
  onRefresh,
}: GoogleDocumentUpdateBannerProps) {
  const [statuses, setStatuses] = useState<DocumentSyncStatus[]>([]);
  const [loading, setLoading] = useState(true);
  const [dismissing, setDismissing] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get<{ statuses: DocumentSyncStatus[] }>(
        `/google-workspace/jobs/${jobId}/documents/sync-status`
      );
      setStatuses(res.data.statuses ?? []);
    } catch {
      setStatuses([]);
    } finally {
      setLoading(false);
    }
  }, [jobId]);

  useEffect(() => {
    void load();
  }, [load]);

  const updated = statuses.filter((s) => s.hasUpdates);

  if (loading || updated.length === 0) return null;

  const handleDismiss = async (documentId: string) => {
    setDismissing(documentId);
    try {
      await api.post(
        `/google-workspace/jobs/${jobId}/documents/${documentId}/acknowledge-sync`
      );
      await load();
      onRefresh?.();
    } finally {
      setDismissing(null);
    }
  };

  return (
    <div className="rounded-lg border border-amber-300 bg-amber-50/80 dark:bg-amber-950/30 p-4 space-y-3">
      <div className="flex items-start gap-3">
        <AlertTriangle className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
        <div className="flex-1 space-y-2">
          <p className="font-medium text-amber-900 dark:text-amber-100">
            Google {updated.length === 1 ? 'file has' : 'files have'} been updated
          </p>
          <ul className="text-sm text-amber-800 dark:text-amber-200 space-y-1">
            {updated.map((s) => (
              <li key={s.documentId}>
                <span className="font-medium">{s.title || s.docType}</span>
                {s.remoteModifiedAt && (
                  <span className="text-amber-700 dark:text-amber-300">
                    {' '}
                    — modified{' '}
                    {new Date(s.remoteModifiedAt).toLocaleString(undefined, {
                      dateStyle: 'medium',
                      timeStyle: 'short',
                    })}
                  </span>
                )}
              </li>
            ))}
          </ul>
        </div>
        <Button variant="outline" size="sm" onClick={() => void load()} className="shrink-0">
          <RefreshCw className="h-3.5 w-3.5" />
        </Button>
      </div>

      {!readOnly && (
        <div className="flex flex-wrap gap-2 pl-8">
          {updated
            .filter((s) => s.docType === 'document')
            .map((s) => (
              <Button
                key={s.documentId}
                size="sm"
                onClick={() => onReimportBrief?.(s.documentId)}
              >
                Re-import brief
              </Button>
            ))}
          {updated.map((s) => (
            <Button
              key={`dismiss-${s.documentId}`}
              variant="outline"
              size="sm"
              disabled={dismissing === s.documentId}
              onClick={() => void handleDismiss(s.documentId)}
            >
              {dismissing === s.documentId ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                'Mark as reviewed'
              )}
            </Button>
          ))}
        </div>
      )}
    </div>
  );
}
